<script lang="ts">
  import type { TicketTierQuote } from '../../lib/types';
  import { formatPrice } from '../format';

  export let tiers: TicketTierQuote[] = [];
  export let currency = 'eur';

  // Switched-off tiers stay hidden; windowed/sold-out tiers show dimmed so
  // buyers can see the whole pricing ladder (early bird → next prices).
  $: visible = tiers.filter(tier => tier.active !== false);

  type Badge = { text: string; kind: 'muted' | 'scarce' | 'upcoming' };

  function badge(tier: TicketTierQuote): Badge | null {
    if (tier.onSale === false) {
      if (tier.salesStart && Date.parse(tier.salesStart) > Date.now()) {
        const opens = new Date(tier.salesStart).toLocaleString(undefined, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        });
        return { text: `Releases ${opens}`, kind: 'upcoming' };
      }
      return { text: 'Sale ended', kind: 'muted' };
    }
    if (tier.soldOut) return { text: 'Sold out', kind: 'muted' };
    if (!tier.isUnlimited && tier.available <= 8) {
      return { text: `${tier.available} left`, kind: 'scarce' };
    }
    return null;
  }
</script>

{#if visible.length > 0}
  <ul class="ladder" aria-label="Ticket prices">
    {#each visible as tier (tier.id)}
      {@const tierBadge = badge(tier)}
      <li class="ladder__row" class:ladder__row--off={tier.onSale === false || tier.soldOut}>
        <span class="ladder__info">
          <span class="ladder__name">{tier.name}</span>
          {#if tier.description}
            <span class="ladder__desc">{tier.description}</span>
          {/if}
        </span>
        {#if tierBadge}
          <span
            class="ladder__badge"
            class:ladder__badge--scarce={tierBadge.kind === 'scarce'}
            class:ladder__badge--upcoming={tierBadge.kind === 'upcoming'}
          >
            {tierBadge.text}
          </span>
        {/if}
        <span class="ladder__price">{formatPrice(tier.currentPrice, currency)}</span>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .ladder {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .ladder__row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) 0;
    border-bottom: var(--border-subtle);
    font-size: var(--font-size-sm);
  }

  .ladder__row:last-child {
    border-bottom: none;
  }

  .ladder__row--off .ladder__name,
  .ladder__row--off .ladder__price {
    color: var(--color-text-muted);
  }

  .ladder__info {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    flex: 1;
  }

  .ladder__name {
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .ladder__desc {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .ladder__badge {
    flex-shrink: 0;
    font-size: var(--font-size-2xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 1px var(--space-2);
    border-radius: var(--radius-pill);
    border: var(--border-subtle);
    color: var(--color-text-muted);
  }

  .ladder__badge--scarce {
    color: var(--color-text-amber);
    border-color: rgb(var(--accent-rgb) / 0.4);
  }

  .ladder__badge--upcoming {
    color: var(--color-text-secondary);
  }

  .ladder__price {
    flex-shrink: 0;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--color-text-amber);
  }
</style>
